import { getSupabase } from './supabase';
import { getIdentity, type Identity } from './identity';
import { PHYSICS_VERSION } from '../game/physics-versions';
import { isGhostRecording, type GhostRecording } from '../timing/Ghost';

// Hide laps recorded before the May 4 2026 physics + track overhaul from the
// public boards. Mirrors the cutoff baked into supabase/migrations/0012.
const LEADERBOARD_CUTOFF_ISO = '2026-05-04T00:00:00Z';

export interface SubmitLapInput {
  totalMs: number;
  sectorMs: [number, number, number];
  trackId: string;
  ghost?: GhostRecording | null;
}

export interface SubmitLapResult {
  ok: boolean;
  error?: string;
  /** Rank in the all-time board. Only meaningful when ok = true. */
  rank?: number;
  lapId?: string;
  hasGhost?: boolean;
}

export interface LeaderboardEntry {
  id: string;
  rank: number;
  handle: string;
  homeworld: string | null;
  total_ms: number;
  sector1_ms: number;
  sector2_ms: number;
  sector3_ms: number;
  device_id: string;
  user_id: string | null;
  created_at: string;
  has_ghost: boolean;
}

export async function submitLap(input: SubmitLapInput): Promise<SubmitLapResult> {
  const sb = getSupabase();
  const id = getIdentity();
  if (!sb || !id) return { ok: false, error: 'Backend not configured' };

  const total = Math.max(1, Math.round(input.totalMs));
  const [s1, s2, s3] = input.sectorMs.map((s) => Math.max(1, Math.round(s))) as [number, number, number];

  const baseArgs = {
    p_device_id: id.deviceId,
    p_device_secret: id.deviceSecret,
    p_track_id: input.trackId,
    p_total_ms: total,
    p_sector1_ms: s1,
    p_sector2_ms: s2,
    p_sector3_ms: s3,
    p_physics_version: PHYSICS_VERSION,
  };

  if (input.ghost) {
    const { data, error } = await sb.rpc('tst_submit_lap_with_ghost', {
      ...baseArgs,
      p_ghost: input.ghost,
    });
    if (error) {
      if (isMissingGhostSubmitRpc(error)) {
        return submitLapWithoutGhost(baseArgs);
      }
      return { ok: false, error: error.message };
    }
    const row = Array.isArray(data) ? data[0] : data;
    return {
      ok: true,
      rank: readNumber(row, 'rank'),
      lapId: readString(row, 'lap_id'),
      hasGhost: readBoolean(row, 'has_ghost'),
    };
  }

  return submitLapWithoutGhost(baseArgs);
}

type SubmitLapRpcArgs = {
  p_device_id: string;
  p_device_secret: string;
  p_track_id: string;
  p_total_ms: number;
  p_sector1_ms: number;
  p_sector2_ms: number;
  p_sector3_ms: number;
  p_physics_version: string;
};

async function submitLapWithoutGhost(baseArgs: SubmitLapRpcArgs): Promise<SubmitLapResult> {
  const sb = getSupabase();
  if (!sb) return { ok: false, error: 'Backend not configured' };
  const { data, error } = await sb.rpc('tst_submit_lap', baseArgs);
  if (error) return { ok: false, error: error.message };

  return { ok: true, rank: typeof data === 'number' ? data : undefined, hasGhost: false };
}

function isMissingGhostSubmitRpc(error: { code?: string; message?: string }): boolean {
  const message = error.message ?? '';
  return error.code === 'PGRST202'
    || (message.includes('tst_submit_lap_with_ghost') && message.includes('Could not find the function'));
}

export async function fetchLapGhost(
  lapId: string,
  trackId: string,
  physicsVersion = PHYSICS_VERSION,
): Promise<GhostRecording | null> {
  const sb = getSupabase();
  if (!sb) return null;
  // Visual replay is keyframe-driven (GhostShip.step reads recorded states),
  // so a v4 ghost from any physics version is playable. Only v3 (no stored
  // height) needs an exact version match because it relies on live ground
  // sampling against possibly-retuned terrain.
  const { data, error } = await sb
    .from('lap_ghosts')
    .select('payload, physics_version')
    .eq('lap_id', lapId)
    .eq('track_id', trackId)
    .maybeSingle();
  if (error || !data) return null;
  const payload = (data as { payload?: unknown }).payload;
  if (!isGhostRecording(payload)) return null;
  if (payload.trackId !== trackId) return null;
  if (payload.schemaVersion < 4 && payload.physicsVersion !== physicsVersion) return null;
  return payload;
}

export type Board = 'alltime' | 'weekly';
export type LeaderboardMode = 'top' | 'personalBest';

export async function fetchLeaderboard(
  board: Board,
  trackId: string,
  limit = 100,
  mode: LeaderboardMode = 'top',
): Promise<LeaderboardEntry[]> {
  const sourceRows = await fetchLeaderboardFromLaps(board, trackId, mode === 'personalBest' ? Math.max(500, limit * 10) : limit);
  if (sourceRows) {
    return mode === 'personalBest'
      ? oneBestPerRacer(sourceRows, limit)
      : withLeaderboardRanks(sourceRows.slice(0, limit));
  }

  const sb = getSupabase();
  if (!sb) return [];
  const view = leaderboardView(board, mode);
  const { data, error } = await sb
    .from(view)
    .select('id, rank, handle, homeworld, total_ms, sector1_ms, sector2_ms, sector3_ms, device_id, user_id, created_at, has_ghost')
    .eq('track_id', trackId)
    .order('total_ms', { ascending: true })
    .limit(limit);
  if (!error && data) return data as LeaderboardEntry[];

  const legacy = await sb
    .from(view)
    .select('id, rank, handle, total_ms, sector1_ms, sector2_ms, sector3_ms, device_id, user_id, created_at')
    .eq('track_id', trackId)
    .order('total_ms', { ascending: true })
    .limit(limit);
  if (!legacy.error && legacy.data) {
    return (legacy.data as Omit<LeaderboardEntry, 'homeworld' | 'has_ghost'>[])
      .map((row) => ({ ...row, homeworld: null, has_ghost: false }));
  }

  if (mode === 'personalBest') {
    console.warn('[twin-suns-turbo] best-per-racer leaderboard view unavailable; falling back to top-times data', error?.message ?? legacy.error?.message);
    return fetchPersonalBestFallback(board, trackId, limit);
  }

  if (error || legacy.error) {
    console.warn('[twin-suns-turbo] leaderboard fetch failed', error?.message ?? legacy.error?.message);
  }
  return [];
}

export async function fetchOwnPersonalBest(
  board: Board,
  trackId: string,
): Promise<LeaderboardEntry | null> {
  const sb = getSupabase();
  const id = getIdentity();
  if (!sb || !id) return null;

  const view = leaderboardView(board, 'personalBest');
  let query = sb
    .from(view)
    .select('id, rank, handle, homeworld, total_ms, sector1_ms, sector2_ms, sector3_ms, device_id, user_id, created_at, has_ghost')
    .eq('track_id', trackId);
  query = applyOwnIdentityFilter(query, id);
  const { data, error } = await query
    .order('total_ms', { ascending: true })
    .limit(1);
  if (!error && data && data.length > 0) return data[0] as LeaderboardEntry;

  let legacyQuery = sb
    .from(view)
    .select('id, rank, handle, total_ms, sector1_ms, sector2_ms, sector3_ms, device_id, user_id, created_at')
    .eq('track_id', trackId);
  legacyQuery = applyOwnIdentityFilter(legacyQuery, id);
  const legacy = await legacyQuery
    .order('total_ms', { ascending: true })
    .limit(1);
  if (!legacy.error && legacy.data && legacy.data.length > 0) {
    const row = legacy.data[0] as Omit<LeaderboardEntry, 'homeworld' | 'has_ghost'>;
    return { ...row, homeworld: null, has_ghost: false };
  }

  return fetchOwnPersonalBestFromLaps(board, trackId, id);
}

interface LeaderboardLapRow {
  id: string;
  total_ms: number;
  sector1_ms: number;
  sector2_ms: number;
  sector3_ms: number;
  device_id: string;
  user_id: string | null;
  created_at: string;
  devices?: HandleRow | HandleRow[] | null;
  users?: HandleRow | HandleRow[] | null;
  lap_ghosts?: LapGhostRow | LapGhostRow[] | null;
}

interface HandleRow {
  handle?: unknown;
  homeworld?: unknown;
}

interface LapGhostRow {
  lap_id?: unknown;
}

interface LeaderboardIdentityRow {
  device_id: string;
  user_id: string | null;
}

async function fetchLeaderboardFromLaps(
  board: Board,
  trackId: string,
  limit: number,
): Promise<LeaderboardEntry[] | null> {
  const rowsWithHomeworld = await fetchLeaderboardRowsFromLaps(board, trackId, limit, true, true);
  if (rowsWithHomeworld !== null) return rowsWithHomeworld;
  const rowsWithHomeworldNoGhost = await fetchLeaderboardRowsFromLaps(board, trackId, limit, true, false);
  if (rowsWithHomeworldNoGhost !== null) return rowsWithHomeworldNoGhost;
  const rowsWithoutHomeworld = await fetchLeaderboardRowsFromLaps(board, trackId, limit, false, true);
  if (rowsWithoutHomeworld !== null) return rowsWithoutHomeworld;
  return fetchLeaderboardRowsFromLaps(board, trackId, limit, false, false);
}

async function fetchOwnPersonalBestFromLaps(
  board: Board,
  trackId: string,
  id: Identity,
): Promise<LeaderboardEntry | null> {
  const own = await fetchOwnBestRowFromLaps(board, trackId, id, true, true)
    ?? await fetchOwnBestRowFromLaps(board, trackId, id, true, false)
    ?? await fetchOwnBestRowFromLaps(board, trackId, id, false, true)
    ?? await fetchOwnBestRowFromLaps(board, trackId, id, false, false);
  if (!own) return null;

  const fasterRacerCount = await countFasterPersonalBestsFromLaps(board, trackId, own.total_ms);
  return { ...own, rank: fasterRacerCount + 1 };
}

async function fetchOwnBestRowFromLaps(
  board: Board,
  trackId: string,
  id: Identity,
  includeHomeworld: boolean,
  includeGhost: boolean,
): Promise<LeaderboardEntry | null> {
  const sb = getSupabase();
  if (!sb) return null;
  const profileColumns = includeHomeworld
    ? 'devices!inner(handle, homeworld), users(handle, homeworld)'
    : 'devices!inner(handle), users(handle)';
  const ghostColumns = includeGhost ? ', lap_ghosts(lap_id)' : '';
  let query = sb
    .from('laps')
    .select(`id, total_ms, sector1_ms, sector2_ms, sector3_ms, device_id, user_id, created_at, ${profileColumns}${ghostColumns}`)
    .eq('track_id', trackId)
    .gte('created_at', LEADERBOARD_CUTOFF_ISO);
  query = applyOwnIdentityFilter(query, id)
    .order('total_ms', { ascending: true })
    .limit(1);

  if (board === 'weekly') {
    query = query.gte('created_at', isoWeekStartUtc().toISOString());
  }

  const { data, error } = await query;
  if (error || !data || data.length === 0) return null;
  return rowToLeaderboardEntry(data[0] as unknown as LeaderboardLapRow);
}

function applyOwnIdentityFilter<Query extends {
  eq: (column: string, value: unknown) => Query;
  is: (column: string, value: null) => Query;
}>(query: Query, id: Identity): Query {
  if (id.authUserId) return query.eq('user_id', id.authUserId);
  return query.eq('device_id', id.deviceId).is('user_id', null);
}

async function countFasterPersonalBestsFromLaps(
  board: Board,
  trackId: string,
  totalMs: number,
): Promise<number> {
  const sb = getSupabase();
  if (!sb) return 0;
  let query = sb
    .from('laps')
    .select('device_id, user_id')
    .eq('track_id', trackId)
    .lt('total_ms', totalMs)
    .gte('created_at', LEADERBOARD_CUTOFF_ISO)
    .order('total_ms', { ascending: true })
    .limit(10_000);

  if (board === 'weekly') {
    query = query.gte('created_at', isoWeekStartUtc().toISOString());
  }

  const { data, error } = await query;
  if (error || !data) return 0;
  const fasterRacers = new Set<string>();
  for (const row of data as LeaderboardIdentityRow[]) {
    fasterRacers.add(row.user_id ?? row.device_id);
  }
  return fasterRacers.size;
}

async function fetchLeaderboardRowsFromLaps(
  board: Board,
  trackId: string,
  limit: number,
  includeHomeworld: boolean,
  includeGhost: boolean,
): Promise<LeaderboardEntry[] | null> {
  const sb = getSupabase();
  if (!sb) return [];
  const profileColumns = includeHomeworld
    ? 'devices!inner(handle, homeworld), users(handle, homeworld)'
    : 'devices!inner(handle), users(handle)';
  const ghostColumns = includeGhost ? ', lap_ghosts(lap_id)' : '';
  let query = sb
    .from('laps')
    .select(`id, total_ms, sector1_ms, sector2_ms, sector3_ms, device_id, user_id, created_at, ${profileColumns}${ghostColumns}`)
    .eq('track_id', trackId)
    .gte('created_at', LEADERBOARD_CUTOFF_ISO)
    .order('total_ms', { ascending: true })
    .limit(limit);

  if (board === 'weekly') {
    query = query.gte('created_at', isoWeekStartUtc().toISOString());
  }

  const { data, error } = await query;
  if (error || !data) {
    if (error && !includeHomeworld && !includeGhost) console.warn('[twin-suns-turbo] direct leaderboard fetch failed', error.message);
    return null;
  }

  return (data as unknown as LeaderboardLapRow[])
    .map(rowToLeaderboardEntry)
    .filter((row): row is LeaderboardEntry => row !== null);
}

function rowToLeaderboardEntry(row: LeaderboardLapRow): LeaderboardEntry | null {
  const handle = readHandle(row.users) ?? readHandle(row.devices);
  if (!handle) return null;
  const homeworld = readHomeworld(row.users) ?? readHomeworld(row.devices);
  return {
    id: row.id,
    rank: 0,
    handle,
    homeworld,
    total_ms: row.total_ms,
    sector1_ms: row.sector1_ms,
    sector2_ms: row.sector2_ms,
    sector3_ms: row.sector3_ms,
    device_id: row.device_id,
    user_id: row.user_id,
    created_at: row.created_at,
    has_ghost: hasEmbeddedGhost(row.lap_ghosts),
  };
}

function readHandle(row: HandleRow | HandleRow[] | null | undefined): string | null {
  const handleRow = Array.isArray(row) ? row[0] : row;
  return typeof handleRow?.handle === 'string' ? handleRow.handle : null;
}

function readHomeworld(row: HandleRow | HandleRow[] | null | undefined): string | null {
  const handleRow = Array.isArray(row) ? row[0] : row;
  return typeof handleRow?.homeworld === 'string' ? handleRow.homeworld : null;
}

function isoWeekStartUtc(now = new Date()): Date {
  const start = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate()));
  const daysSinceMonday = (start.getUTCDay() + 6) % 7;
  start.setUTCDate(start.getUTCDate() - daysSinceMonday);
  return start;
}

async function fetchPersonalBestFallback(
  board: Board,
  trackId: string,
  limit: number,
): Promise<LeaderboardEntry[]> {
  const sb = getSupabase();
  if (!sb) return [];
  const fallbackLimit = Math.max(500, limit * 10);
  const { data, error } = await sb
    .from(leaderboardView(board, 'top'))
    .select('id, rank, handle, homeworld, total_ms, sector1_ms, sector2_ms, sector3_ms, device_id, user_id, created_at, has_ghost')
    .eq('track_id', trackId)
    .order('total_ms', { ascending: true })
    .limit(fallbackLimit);
  if (!error && data) return oneBestPerRacer(data as LeaderboardEntry[], limit);

  const legacy = await sb
    .from(leaderboardView(board, 'top'))
    .select('id, rank, handle, total_ms, sector1_ms, sector2_ms, sector3_ms, device_id, user_id, created_at')
    .eq('track_id', trackId)
    .order('total_ms', { ascending: true })
    .limit(fallbackLimit);
  if (legacy.error || !legacy.data) {
    console.warn('[twin-suns-turbo] leaderboard fallback failed', error?.message ?? legacy.error?.message);
    return [];
  }
  return oneBestPerRacer((legacy.data as Omit<LeaderboardEntry, 'homeworld' | 'has_ghost'>[])
    .map((row) => ({ ...row, homeworld: null, has_ghost: false })), limit);
}

function oneBestPerRacer(rows: LeaderboardEntry[], limit: number): LeaderboardEntry[] {
  const seen = new Set<string>();
  const best: LeaderboardEntry[] = [];

  for (const row of rows) {
    const racerId = row.user_id ?? row.device_id;
    if (seen.has(racerId)) continue;
    seen.add(racerId);
    best.push(row);
    if (best.length >= limit) break;
  }

  return withLeaderboardRanks(best);
}

function withLeaderboardRanks(rows: LeaderboardEntry[]): LeaderboardEntry[] {
  let previousTime: number | null = null;
  let previousRank = 0;
  return rows.map((row, index) => {
    const rank = row.total_ms === previousTime ? previousRank : index + 1;
    previousTime = row.total_ms;
    previousRank = rank;
    return { ...row, rank };
  });
}

function leaderboardView(board: Board, mode: LeaderboardMode): string {
  if (mode === 'personalBest') {
    return board === 'alltime' ? 'lb_alltime_personal_best' : 'lb_weekly_personal_best';
  }
  return board === 'alltime' ? 'lb_alltime' : 'lb_weekly';
}

function hasEmbeddedGhost(row: LapGhostRow | LapGhostRow[] | null | undefined): boolean {
  const ghostRow = Array.isArray(row) ? row[0] : row;
  return typeof ghostRow?.lap_id === 'string';
}

function readNumber(row: unknown, key: string): number | undefined {
  if (!row || typeof row !== 'object') return undefined;
  const value = (row as Record<string, unknown>)[key];
  return typeof value === 'number' ? value : undefined;
}

function readString(row: unknown, key: string): string | undefined {
  if (!row || typeof row !== 'object') return undefined;
  const value = (row as Record<string, unknown>)[key];
  return typeof value === 'string' ? value : undefined;
}

function readBoolean(row: unknown, key: string): boolean | undefined {
  if (!row || typeof row !== 'object') return undefined;
  const value = (row as Record<string, unknown>)[key];
  return typeof value === 'boolean' ? value : undefined;
}
